import {useContext} from 'react'

import PropTypes from 'prop-types'

import {BLOCKS, INLINES} from '@contentful/rich-text-types'

import Context from '@s-ui/react-context'
import DocumentContentful from '@adv-mt/mt-document-contentful/src'
import NewsletterSubscription from '@adv-mt/mt-newsletter-subscription'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

import {NEWS_DETAIL_BASE_CLASS} from '../config.js'
import {EmbeddedEntriesDispatcher} from './EmbeddedEntriesDispatcher.js'

const BASE_CLASS = `${NEWS_DETAIL_BASE_CLASS}-articleBody`
const NEWSLETTER_WRAPPER_CLASS = `${BASE_CLASS}NewsletterWrapper`

const ArticleParagraph = (_, children) => {
  const PARAGRAPH_CLASS = `${BASE_CLASS}Paragraph`
  return <p className={PARAGRAPH_CLASS}>{children}</p>
}

const ArticleHeading = (_, children) => {
  return (
    <div className={`${BASE_CLASS}Heading`}>
      <TitleBasic title={children.join(' ')} as={titleBasicTags.h2} size={titleBasicSizes.s} />
    </div>
  )
}

const ArticleBlockquote = (_, children) => {
  const QUOTE_CLASS = `${BASE_CLASS}Quote`
  return <blockquote className={QUOTE_CLASS}>{children}</blockquote>
}

const ArticleLink = (_, children) => {
  const LINK_CLASS = `${BASE_CLASS}Link`
  const {domain} = useContext(Context)
  const domainConfig = domain.get('config')
  const {NEWS_HOME_PATH_PATTERN} = domainConfig.get('page')

  return (
    <a className={LINK_CLASS} href={_.data?.uri || NEWS_HOME_PATH_PATTERN} target="_blank">
      {children}
    </a>
  )
}

const ArticleUnorderedList = (_, children) => {
  const LIST_CLASS = `${BASE_CLASS}UnorderedList`

  return <ul className={LIST_CLASS}>{children}</ul>
}

const getMiddleParagraphIndex = content => {
  const PARAGRAPH_NODE_TYPE = 'paragraph'

  const paragraphIndexArr = content?.content
    ?.map((element, i) => {
      if (element.nodeType === PARAGRAPH_NODE_TYPE) return i
    })
    .filter(Boolean)

  const middleIndex = Math.floor(paragraphIndexArr.length / 2)

  return paragraphIndexArr[middleIndex]
}

const splitContentIntoChunks = ({content, index}) => {
  const initialContentChunk = {
    ...content,
    content: content?.content?.slice(0, index + 1)
  }
  const lastContentChunk = {
    ...content,
    content: content?.content?.slice(index + 1)
  }

  return [initialContentChunk, lastContentChunk]
}

const getContentChunks = content => {
  const sliceIndex = getMiddleParagraphIndex(content)

  const [initialContentChunk, lastContentChunk] = splitContentIntoChunks({content, index: sliceIndex})

  return [initialContentChunk, lastContentChunk]
}

export const ArticleBody = ({
  content,
  customContentConfig,
  hasBodyNewsletter,
  pageName,
  professionalSellerAds,
  professionalSellerAdsListingUrl,
  vehicles
}) => {
  const documentContentfulOptions = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: ArticleParagraph,
      [BLOCKS.HEADING_2]: ArticleHeading,
      [BLOCKS.QUOTE]: ArticleBlockquote,
      [INLINES.HYPERLINK]: ArticleLink,
      [BLOCKS.UL_LIST]: ArticleUnorderedList,
      [BLOCKS.EMBEDDED_ENTRY]: entryContent => (
        <EmbeddedEntriesDispatcher
          entryContent={entryContent}
          customContentConfig={customContentConfig}
          professionalSellerAds={professionalSellerAds}
          professionalSellerAdsListingUrl={professionalSellerAdsListingUrl}
          vehicles={vehicles}
        />
      )
    }
  }

  let initialContentChunk
  let lastContentChunk

  if (content && hasBodyNewsletter) {
    const [initialChunk, lastChunk] = getContentChunks(content)
    initialContentChunk = initialChunk
    lastContentChunk = lastChunk
  }

  return (
    <div className={BASE_CLASS}>
      {hasBodyNewsletter ? (
        <>
          <DocumentContentful content={initialContentChunk} rendererOptions={documentContentfulOptions} />
          <div className={NEWSLETTER_WRAPPER_CLASS}>
            <NewsletterSubscription pageName={pageName} />
          </div>
          <DocumentContentful content={lastContentChunk} rendererOptions={documentContentfulOptions} />
        </>
      ) : (
        <DocumentContentful content={content} rendererOptions={documentContentfulOptions} />
      )}
    </div>
  )
}

ArticleBody.propTypes = {
  content: PropTypes.array,
  customContentConfig: PropTypes.object,
  hasBodyNewsletter: PropTypes.bool,
  professionalSellerAds: PropTypes.array,
  professionalSellerAdsListingUrl: PropTypes.string,
  pageName: PropTypes.bool,
  vehicles: PropTypes.array
}
